/**
 * The 193 United Nations member states and the two permanent non-member observer states
 * (the Holy See and the State of Palestine), with capital, continent and ISO 3166-1
 * alpha-2 code.
 *
 * ## Sources
 *
 * - Membership and the names in the `un` field: the United Nations member-states list
 *   (un.org/en/about-us/member-states, 193 members) and the Permanent Observers pages.
 *   The Holy See and the State of Palestine are the two non-member observer states.
 * - Capitals: the governments' own designations, cross-checked against the UN Secretariat
 *   and standard references. The CIA World Factbook, the usual source for this, was
 *   retired in 2026, so it is not cited. Where a state has more than one seat of
 *   government the capital field lists each with its role.
 * - Continents: the seven-continent model, with each state assigned to one continent
 *   following the UN geoscheme (M49): North America includes Central America and the
 *   Caribbean; Oceania includes Australia, New Zealand, Melanesia, Micronesia and
 *   Polynesia; Armenia, Azerbaijan, Cyprus, Georgia, Kazakhstan and Türkiye are placed in
 *   Asia, and Russia in Europe. The `alsoIn` field marks the states that straddle a
 *   boundary. Antarctica has no states.
 *
 * ## The row format
 *
 * `name|capital|continent|code|un name|also in`, with trailing empty fields left off.
 * Continent codes: AF Africa, AS Asia, EU Europe, NA North America, SA South America,
 * OC Oceania. Strings, not objects, so each country stays on one line.
 */

import {
  randomBelow,
  sample,
  type Uint32Source,
} from "@/lib/tool-kit/random-fun/rng";

export type Continent = "AF" | "AS" | "EU" | "NA" | "SA" | "OC";

export const CONTINENTS: readonly Continent[] = [
  "AF",
  "AS",
  "EU",
  "NA",
  "OC",
  "SA",
];

export interface Country {
  name: string;
  capital: string;
  continent: Continent;
  /** ISO 3166-1 alpha-2. */
  code: string;
  /** The name on the UN member-states list, where it differs from `name`. */
  unName?: string;
  /** The other continent a transcontinental state also touches. */
  alsoIn?: Continent;
  observer: boolean;
}

const MEMBER_ROWS: readonly string[] = [
  "Afghanistan|Kabul|AS|AF",
  "Albania|Tirana|EU|AL",
  "Algeria|Algiers|AF|DZ",
  "Andorra|Andorra la Vella|EU|AD",
  "Angola|Luanda|AF|AO",
  "Antigua and Barbuda|Saint John's|NA|AG",
  "Argentina|Buenos Aires|SA|AR",
  "Armenia|Yerevan|AS|AM||EU",
  "Australia|Canberra|OC|AU",
  "Austria|Vienna|EU|AT",
  "Azerbaijan|Baku|AS|AZ||EU",
  "Bahamas|Nassau|NA|BS|Bahamas (The)",
  "Bahrain|Manama|AS|BH",
  "Bangladesh|Dhaka|AS|BD",
  "Barbados|Bridgetown|NA|BB",
  "Belarus|Minsk|EU|BY",
  "Belgium|Brussels|EU|BE",
  "Belize|Belmopan|NA|BZ",
  "Benin|Porto-Novo (official); Cotonou (seat of government)|AF|BJ",
  "Bhutan|Thimphu|AS|BT",
  "Bolivia|Sucre (constitutional); La Paz (seat of government)|SA|BO|Bolivia (Plurinational State of)",
  "Bosnia and Herzegovina|Sarajevo|EU|BA",
  "Botswana|Gaborone|AF|BW",
  "Brazil|Brasília|SA|BR",
  "Brunei|Bandar Seri Begawan|AS|BN|Brunei Darussalam",
  "Bulgaria|Sofia|EU|BG",
  "Burkina Faso|Ouagadougou|AF|BF",
  "Burundi|Gitega (political); Bujumbura (economic)|AF|BI",
  "Cabo Verde (Cape Verde)|Praia|AF|CV|Cabo Verde",
  "Cambodia|Phnom Penh|AS|KH",
  "Cameroon|Yaoundé|AF|CM",
  "Canada|Ottawa|NA|CA",
  "Central African Republic|Bangui|AF|CF",
  "Chad|N'Djamena|AF|TD",
  "Chile|Santiago|SA|CL",
  "China|Beijing|AS|CN|China (the People's Republic of)",
  "Colombia|Bogotá|SA|CO",
  "Comoros|Moroni|AF|KM",
  "Congo (Republic of the)|Brazzaville|AF|CG|Congo",
  "Costa Rica|San José|NA|CR",
  "Côte d'Ivoire (Ivory Coast)|Yamoussoukro (official); Abidjan (seat of government)|AF|CI|Côte d'Ivoire",
  "Croatia|Zagreb|EU|HR",
  "Cuba|Havana|NA|CU",
  "Cyprus|Nicosia|AS|CY||EU",
  "Czechia (Czech Republic)|Prague|EU|CZ|Czechia",
  "Democratic Republic of the Congo|Kinshasa|AF|CD",
  "Denmark|Copenhagen|EU|DK",
  "Djibouti|Djibouti|AF|DJ",
  "Dominica|Roseau|NA|DM",
  "Dominican Republic|Santo Domingo|NA|DO",
  "Ecuador|Quito|SA|EC",
  "Egypt|Cairo|AF|EG||AS",
  "El Salvador|San Salvador|NA|SV",
  "Equatorial Guinea|Ciudad de la Paz (moved from Malabo in 2026)|AF|GQ",
  "Eritrea|Asmara|AF|ER",
  "Estonia|Tallinn|EU|EE",
  "Eswatini|Mbabane (administrative); Lobamba (legislative and royal)|AF|SZ",
  "Ethiopia|Addis Ababa|AF|ET",
  "Fiji|Suva|OC|FJ",
  "Finland|Helsinki|EU|FI",
  "France|Paris|EU|FR",
  "Gabon|Libreville|AF|GA",
  "Gambia|Banjul|AF|GM|Gambia (Republic of The)",
  "Georgia|Tbilisi|AS|GE||EU",
  "Germany|Berlin|EU|DE",
  "Ghana|Accra|AF|GH",
  "Greece|Athens|EU|GR",
  "Grenada|Saint George's|NA|GD",
  "Guatemala|Guatemala City|NA|GT",
  "Guinea|Conakry|AF|GN",
  "Guinea-Bissau|Bissau|AF|GW|Guinea Bissau",
  "Guyana|Georgetown|SA|GY",
  "Haiti|Port-au-Prince|NA|HT",
  "Honduras|Tegucigalpa|NA|HN",
  "Hungary|Budapest|EU|HU",
  "Iceland|Reykjavík|EU|IS",
  "India|New Delhi|AS|IN",
  "Indonesia|Jakarta|AS|ID",
  "Iran|Tehran|AS|IR|Iran (Islamic Republic of)",
  "Iraq|Baghdad|AS|IQ",
  "Ireland|Dublin|EU|IE",
  "Israel|Jerusalem|AS|IL",
  "Italy|Rome|EU|IT",
  "Jamaica|Kingston|NA|JM",
  "Japan|Tokyo|AS|JP",
  "Jordan|Amman|AS|JO",
  "Kazakhstan|Astana|AS|KZ||EU",
  "Kenya|Nairobi|AF|KE",
  "Kiribati|South Tarawa|OC|KI",
  "Kuwait|Kuwait City|AS|KW",
  "Kyrgyzstan|Bishkek|AS|KG",
  "Laos|Vientiane|AS|LA|Lao People's Democratic Republic",
  "Latvia|Riga|EU|LV",
  "Lebanon|Beirut|AS|LB",
  "Lesotho|Maseru|AF|LS",
  "Liberia|Monrovia|AF|LR",
  "Libya|Tripoli|AF|LY",
  "Liechtenstein|Vaduz|EU|LI",
  "Lithuania|Vilnius|EU|LT",
  "Luxembourg|Luxembourg City|EU|LU",
  "Madagascar|Antananarivo|AF|MG",
  "Malawi|Lilongwe|AF|MW",
  "Malaysia|Kuala Lumpur (Putrajaya is the administrative centre)|AS|MY",
  "Maldives|Malé|AS|MV",
  "Mali|Bamako|AF|ML",
  "Malta|Valletta|EU|MT",
  "Marshall Islands|Majuro|OC|MH",
  "Mauritania|Nouakchott|AF|MR",
  "Mauritius|Port Louis|AF|MU",
  "Mexico|Mexico City|NA|MX",
  "Micronesia|Palikir|OC|FM|Micronesia (Federated States of)",
  "Moldova|Chișinău|EU|MD|Republic of Moldova",
  "Monaco|Monaco|EU|MC",
  "Mongolia|Ulaanbaatar|AS|MN",
  "Montenegro|Podgorica|EU|ME",
  "Morocco|Rabat|AF|MA",
  "Mozambique|Maputo|AF|MZ",
  "Myanmar|Nay Pyi Taw|AS|MM",
  "Namibia|Windhoek|AF|NA",
  "Nauru|Yaren (de facto; no official capital)|OC|NR|Naoero",
  "Nepal|Kathmandu|AS|NP",
  "Netherlands|Amsterdam (the government sits in The Hague)|EU|NL|Netherlands (Kingdom of the)",
  "New Zealand|Wellington|OC|NZ",
  "Nicaragua|Managua|NA|NI",
  "Niger|Niamey|AF|NE",
  "Nigeria|Abuja|AF|NG",
  "North Korea|Pyongyang|AS|KP|Democratic People's Republic of Korea",
  "North Macedonia|Skopje|EU|MK",
  "Norway|Oslo|EU|NO",
  "Oman|Muscat|AS|OM",
  "Pakistan|Islamabad|AS|PK",
  "Palau|Ngerulmud|OC|PW",
  "Panama|Panama City|NA|PA",
  "Papua New Guinea|Port Moresby|OC|PG",
  "Paraguay|Asunción|SA|PY",
  "Peru|Lima|SA|PE",
  "Philippines|Manila|AS|PH",
  "Poland|Warsaw|EU|PL",
  "Portugal|Lisbon|EU|PT",
  "Qatar|Doha|AS|QA",
  "Romania|Bucharest|EU|RO",
  "Russia|Moscow|EU|RU|Russian Federation|AS",
  "Rwanda|Kigali|AF|RW",
  "Saint Kitts and Nevis|Basseterre|NA|KN",
  "Saint Lucia|Castries|NA|LC",
  "Saint Vincent and the Grenadines|Kingstown|NA|VC",
  "Samoa|Apia|OC|WS",
  "San Marino|San Marino|EU|SM",
  "São Tomé and Príncipe|São Tomé|AF|ST|Sao Tome and Principe",
  "Saudi Arabia|Riyadh|AS|SA",
  "Senegal|Dakar|AF|SN",
  "Serbia|Belgrade|EU|RS",
  "Seychelles|Victoria|AF|SC",
  "Sierra Leone|Freetown|AF|SL",
  "Singapore|Singapore|AS|SG",
  "Slovakia|Bratislava|EU|SK",
  "Slovenia|Ljubljana|EU|SI",
  "Solomon Islands|Honiara|OC|SB",
  "Somalia|Mogadishu|AF|SO",
  "South Africa|Pretoria (executive); Cape Town (legislative); Bloemfontein (judicial)|AF|ZA",
  "South Korea|Seoul|AS|KR|Republic of Korea",
  "South Sudan|Juba|AF|SS",
  "Spain|Madrid|EU|ES",
  "Sri Lanka|Sri Jayawardenepura Kotte (legislative); Colombo (executive and judicial)|AS|LK",
  "Sudan|Khartoum|AF|SD",
  "Suriname|Paramaribo|SA|SR",
  "Sweden|Stockholm|EU|SE",
  "Switzerland|Bern (the federal city)|EU|CH",
  "Syria|Damascus|AS|SY|Syrian Arab Republic",
  "Tajikistan|Dushanbe|AS|TJ",
  "Tanzania|Dodoma|AF|TZ|United Republic of Tanzania",
  "Thailand|Bangkok|AS|TH",
  "Timor-Leste (East Timor)|Dili|AS|TL|Timor-Leste",
  "Togo|Lomé|AF|TG",
  "Tonga|Nukuʻalofa|OC|TO",
  "Trinidad and Tobago|Port of Spain|NA|TT",
  "Tunisia|Tunis|AF|TN",
  "Türkiye (Turkey)|Ankara|AS|TR|Türkiye|EU",
  "Turkmenistan|Ashgabat|AS|TM",
  "Tuvalu|Funafuti|OC|TV",
  "Uganda|Kampala|AF|UG",
  "Ukraine|Kyiv|EU|UA",
  "United Arab Emirates|Abu Dhabi|AS|AE",
  "United Kingdom|London|EU|GB|United Kingdom of Great Britain and Northern Ireland",
  "United States|Washington, D.C.|NA|US|United States of America",
  "Uruguay|Montevideo|SA|UY",
  "Uzbekistan|Tashkent|AS|UZ",
  "Vanuatu|Port Vila|OC|VU",
  "Venezuela|Caracas|SA|VE|Venezuela (Bolivarian Republic of)",
  "Vietnam|Hanoi|AS|VN|Viet Nam",
  "Yemen|Sana'a (Aden is the temporary seat of government)|AS|YE",
  "Zambia|Lusaka|AF|ZM",
  "Zimbabwe|Harare|AF|ZW",
];

const OBSERVER_ROWS: readonly string[] = [
  "Holy See (Vatican City)|Vatican City|EU|VA|Holy See",
  "Palestine|East Jerusalem (declared); Ramallah (seat of government)|AS|PS|State of Palestine",
];

function parseRow(row: string, observer: boolean): Country {
  const [name = "", capital = "", continent = "", code = "", unName, alsoIn] =
    row.split("|");
  return {
    name,
    capital,
    continent: continent as Continent,
    code,
    ...(unName ? { unName } : {}),
    ...(alsoIn ? { alsoIn: alsoIn as Continent } : {}),
    observer,
  };
}

/** All 193 member states, then the 2 observer states, in roughly alphabetical order by common name. */
export const COUNTRIES: readonly Country[] = [
  ...MEMBER_ROWS.map((row) => parseRow(row, false)),
  ...OBSERVER_ROWS.map((row) => parseRow(row, true)),
];

export interface CountryFilter {
  continents: readonly Continent[];
  includeObservers: boolean;
}

/** The countries a filter allows, in list order. */
export function filterCountries(filter: CountryFilter): Country[] {
  return COUNTRIES.filter(
    (country) =>
      filter.continents.includes(country.continent) &&
      (filter.includeObservers || !country.observer)
  );
}

/**
 * Draws `count` countries. Without repeats it is a partial shuffle of the pool, capped at
 * the pool size; with repeats each draw is independent.
 */
export function drawCountries(
  pool: readonly Country[],
  count: number,
  unique: boolean,
  source?: Uint32Source
): Country[] {
  if (pool.length === 0 || count < 1) {
    return [];
  }
  if (unique) {
    return sample(pool, count, source);
  }
  return Array.from({ length: count }, () => {
    const index = randomBelow(pool.length, source);
    return pool[index] as Country;
  });
}
